import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { getCaseById, updateCase } from '../lib/api'
import type { Case } from '../types'

export default function EditCase() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(true)
  const [formData, setFormData] = useState({
    title: '',
    caseType: 'civil_litigation' as 'civil_litigation' | 'trademark_admin' | 'other',
    partyRole: 'plaintiff' as 'plaintiff' | 'defendant' | 'third_party',
    opposingParty: '',
    court: '',
    claimAmount: '',
    filingDate: '',
    caseNumber: '',
    notes: '',
    status: 'pending' as 'pending' | 'active' | 'closed' | 'suspended',
  })
  const [error, setError] = useState('')

  useEffect(() => {
    if (id) {
      loadCase(id)
    } else {
      navigate('/cases')
    }
  }, [id])

  const loadCase = async (caseId: string) => {
    try {
      setDataLoading(true)
      const caseData = await getCaseById(caseId)
      setFormData({
        title: caseData.title,
        caseType: caseData.case_type,
        partyRole: caseData.party_role,
        opposingParty: caseData.opposing_party,
        court: caseData.court || '',
        claimAmount: caseData.claim_amount ? caseData.claim_amount.toString() : '',
        filingDate: caseData.filing_date || '',
        caseNumber: caseData.case_number || '',
        notes: caseData.notes || '',
        status: caseData.status,
      })
    } catch (err) {
      console.error('加载案件失败:', err)
      setError('加载案件失败，请重试')
    } finally {
      setDataLoading(false)
    }
  }

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (field === 'caseType' && value === 'trademark_admin') {
      setFormData(prev => ({ ...prev, court: '北京知识产权法院' }))
    }
  }

  const handleSubmit = async () => {
    if (!id) return
    
    // 待发起状态：只需基本信息
    // 已立案/已开庭/已结案：需要完整信息
    const isPending = formData.status === 'pending'
    
    if (!formData.title || !formData.opposingParty || !formData.court) {
      setError('请填写案由、对方当事人、法院')
      return
    }
    
    // 非待发起状态，立案日期必填
    if (!isPending && !formData.filingDate) {
      setError('请填写立案日期')
      return
    }
    
    setLoading(true)
    setError('')
    
    try {
      const caseData = {
        case_number: isPending ? null : (formData.caseNumber || null),
        title: formData.title,
        case_type: formData.caseType,
        party_role: formData.partyRole,
        opposing_party: formData.opposingParty,
        court: formData.court,
        claim_amount: formData.claimAmount ? parseFloat(formData.claimAmount) : null,
        status: formData.status,
        notes: formData.notes || null,
        filing_date: isPending ? null : formData.filingDate,
      }
      
      await updateCase(id, caseData)
      navigate(`/cases/${id}`)
    } catch (err: any) {
      console.error('更新案件失败:', err)
      setError(err?.message || '更新失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  if (dataLoading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>
  }

  const getStatusHint = () => {
    switch (formData.status) {
      case 'pending':
        return '案件尚未立案，仅填写基本信息即可'
      case 'active':
        return '案件已立案，请填写完整信息'
      case 'closed':
        return '案件已结案，请填写完整信息包括结案日期'
      case 'suspended':
        return '案件已中止，请填写完整信息'
      default:
        return ''
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-8">
        <h2 className="text-xl font-semibold mb-1">编辑案件</h2>
        <p className="text-sm text-gray-500 mb-2">
          修改案件信息，系统将自动更新程序节点。
        </p>
        <p className="text-xs text-indigo-600 mb-4">{getStatusHint()}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* 案件状态选择 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">案件状态 <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: 'pending', label: '待发起', desc: '尚未立案' },
                { value: 'active', label: '进行中', desc: '已立案' },
                { value: 'closed', label: '已结案', desc: '已结案' },
                { value: 'suspended', label: '中止', desc: '中止审理' },
              ].map(s => (
                <button
                  key={s.value}
                  onClick={() => updateField('status', s.value)}
                  className={`py-2.5 rounded-lg text-sm font-medium transition-all border-2 ${
                    formData.status === s.value
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <div>{s.label}</div>
                  <div className="text-xs opacity-60">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">案由 <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.title}
                onChange={e => updateField('title', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">案件类型 <span className="text-red-500">*</span></label>
              <select
                value={formData.caseType}
                onChange={e => updateField('caseType', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="civil_litigation">民事诉讼</option>
                <option value="trademark_admin">商标行政诉讼</option>
                <option value="other">其他</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">我方角色 <span className="text-red-500">*</span></label>
            <div className="flex gap-3">
              {[
                { value: 'plaintiff', label: '原告', active: formData.partyRole === 'plaintiff' },
                { value: 'defendant', label: '被告', active: formData.partyRole === 'defendant' },
                { value: 'third_party', label: '第三人', active: formData.partyRole === 'third_party' },
              ].map(role => (
                <button
                  key={role.value}
                  onClick={() => updateField('partyRole', role.value)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all border-2 ${
                    role.active
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {role.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">对方当事人 <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.opposingParty}
                onChange={e => updateField('opposingParty', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">法院 <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.court}
                onChange={e => updateField('court', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* 立案日期 - 非待发起状态显示 */}
          {formData.status !== 'pending' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">立案日期 <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={formData.filingDate}
                  onChange={e => updateField('filingDate', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">案号</label>
                <input
                  type="text"
                  value={formData.caseNumber}
                  onChange={e => updateField('caseNumber', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          )}

          {/* 待发起状态显示提示 */}
          {formData.status === 'pending' && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                💡 提示：待发起状态只需填写基本信息，立案日期和案号可后续补充。
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">标的金额（元）</label>
              <input
                type="number"
                value={formData.claimAmount}
                onChange={e => updateField('claimAmount', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
              <input
                type="text"
                value={formData.notes}
                onChange={e => updateField('notes', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => navigate(`/cases/${id}`)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '保存中...' : '保存修改'}
          </button>
        </div>
      </div>
    </div>
  )
}
